import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect } from "preact/hooks";
import { IcoCircle, IcoUser, IcoUsers } from "../../components/icons";
import { BrandedLayout, PendingCheckInCard } from "../../components/ui";
import { useHistory, usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { TeamMember } from "../../server/rpc/team";
import type { MimicState } from "../../stores/mimic";
import type { AssignedForm, BrandColor, HistoryItem, User } from "../../types";
import { isAdmin } from "../../types";
import {
  formatDateShort,
  formatPeriodRange,
  formatRelativeDate,
} from "../../utils/format";

type CheckInsPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  currentMemberId: string;
  isAdmin: boolean;
  hasReports: boolean;
  // View state from URL params
  view: "mine" | "team";
  managerId: string | null;
  managerName: string | null;
  // Team data (only for team view)
  team: TeamMember[];
  // Breadcrumb segments for drill-down
  breadcrumbs: { memberId: string; memberName: string | null }[];
  // Pending forms for the current period
  pendingForms: AssignedForm[];
};

const VALID_VIEWS = ["mine", "team"] as const;
type ViewType = (typeof VALID_VIEWS)[number];

export async function load(args: LoaderArgs): Promise<CheckInsPageState> {
  const orgId = args.params.orgId ?? "";
  const rawView = args.url.searchParams.get("view");
  const view: ViewType | null = VALID_VIEWS.includes(rawView as ViewType)
    ? (rawView as ViewType)
    : null;
  const managerId = args.url.searchParams.get("manager") ?? null;

  // Fetch auth, org, orgs list, team, forms, and optionally hierarchy all in parallel
  const [
    authResult,
    orgsResult,
    orgResult,
    teamResult,
    hierarchyResult,
    formsResult,
  ] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
    rpc.team.getMyTeam({
      workspaceId: orgId,
      managerId: managerId ?? undefined,
    }),
    managerId
      ? rpc.team.getTeamHierarchy({
          workspaceId: orgId,
          targetMemberId: managerId,
        })
      : Promise.resolve(null),
    rpc.submissions.myForms({ orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const currentUser = authResult.user as User;
  const currentMember = orgResult.currentMember;
  const userIsAdmin = isAdmin(currentMember.role);

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Determine the effective view
  // Default to "team" if user has reports, otherwise "mine"
  let effectiveView = view;
  if (!effectiveView) {
    effectiveView = teamResult.hasReports ? "team" : "mine";
  }

  // Validate view access
  if (effectiveView === "team" && !teamResult.hasReports && !managerId) {
    effectiveView = "mine";
  }

  const breadcrumbs = hierarchyResult?.segments ?? [];

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    currentMemberId: currentMember.id,
    isAdmin: userIsAdmin,
    hasReports: teamResult.hasReports,
    view: effectiveView,
    managerId,
    managerName: teamResult.manager?.name ?? null,
    team: teamResult.team,
    breadcrumbs,
    pendingForms: formsResult.forms,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Check-ins");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isAdmin: userIsAdmin,
    hasReports,
    view,
    managerId,
    managerName,
    team,
    breadcrumbs,
    pendingForms,
  } = state.value;

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="check-ins"
      isAdmin={userIsAdmin}
      hasReports={hasReports}
    >
      <div class="flex flex-col gap-6">
        {/* Check-in tabs */}
        <CheckInTabs orgId={orgId} activeView={view} hasReports={hasReports} />

        {/* Content based on view */}
        {view === "team" && (
          <TeamView
            orgId={orgId}
            team={team}
            managerId={managerId}
            managerName={managerName}
            breadcrumbs={breadcrumbs}
          />
        )}

        {view === "mine" && (
          <MyCheckInsView orgId={orgId} pendingForms={pendingForms} />
        )}
      </div>
    </BrandedLayout>
  );
}

// ============================================================================
// Check-in Tabs Component
// ============================================================================

function CheckInTabs({
  orgId,
  activeView,
  hasReports,
}: {
  orgId: string;
  activeView: "mine" | "team";
  hasReports: boolean;
}) {
  return (
    <div class="flex items-center gap-1 border-b border-border -mx-1">
      <TabLink
        href={`/orgs/${orgId}/check-ins?view=mine`}
        active={activeView === "mine"}
        label="My Check-ins"
      />
      {hasReports && (
        <TabLink
          href={`/orgs/${orgId}/check-ins?view=team`}
          active={activeView === "team"}
          label="My Team"
          icon={<IcoUsers class="size-4" />}
        />
      )}
    </div>
  );
}

function TabLink({
  href,
  active,
  label,
  icon,
}: {
  href: string;
  active: boolean;
  label: string;
  icon?: preact.JSX.Element;
}) {
  return (
    <a
      href={href}
      class={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors rounded-none focus-visible:rounded ${
        active
          ? "text-text border-text"
          : "text-text-muted hover:text-text border-transparent"
      }`}
    >
      {icon}
      {label}
    </a>
  );
}

// ============================================================================
// Team View Component
// ============================================================================

function TeamView({
  orgId,
  team,
  managerId,
  managerName,
  breadcrumbs,
}: {
  orgId: string;
  team: TeamMember[];
  managerId: string | null;
  managerName: string | null;
  breadcrumbs: { memberId: string; memberName: string | null }[];
}) {
  const isDrilledDown = managerId !== null;
  const reportCount = team.length;

  return (
    <div class="flex flex-col gap-4">
      {/* Breadcrumbs for drill-down */}
      {isDrilledDown && (
        <nav class="flex items-center gap-2 text-sm">
          <a
            href={`/orgs/${orgId}/check-ins?view=team`}
            class="text-text-muted hover:text-text"
          >
            My Team
          </a>
          {breadcrumbs.map((segment, index) => (
            <span key={segment.memberId} class="flex items-center gap-2">
              <span class="text-text-subtle">/</span>
              {index === breadcrumbs.length - 1 ? (
                <span class="text-text font-medium">
                  {segment.memberName ?? "Unknown"}'s Team
                </span>
              ) : (
                <a
                  href={`/orgs/${orgId}/check-ins?view=team&manager=${segment.memberId}`}
                  class="text-text-muted hover:text-text"
                >
                  {segment.memberName ?? "Unknown"}'s Team
                </a>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Context line */}
      <p class="text-text-muted">
        {isDrilledDown
          ? `${reportCount} ${reportCount === 1 ? "person reports" : "people report"} to ${managerName ?? "this manager"}`
          : `${reportCount} ${reportCount === 1 ? "person reports" : "people report"} to you`}
      </p>

      {/* Team member list */}
      {team.length === 0 ? (
        <div class="bg-surface border border-border rounded-lg p-8 text-center">
          <p class="text-text-muted">
            {isDrilledDown
              ? "No one reports to this person."
              : "You don't have any direct reports."}
          </p>
        </div>
      ) : (
        <div class="flex flex-col gap-2">
          {team.map((member) => (
            <TeamMemberRow key={member.id} member={member} orgId={orgId} />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamMemberRow({
  member,
  orgId,
}: {
  member: TeamMember;
  orgId: string;
}) {
  const displayName = member.name ?? member.email;
  const submission = member.latestSubmission;
  const hasSubmission = submission !== null;
  const isUnreviewed = hasSubmission && !submission?.reviewedAt;
  const submissionDate = submission
    ? formatRelativeDate(submission.submittedAt)
    : "Not submitted";

  // Link to submission if exists (primary action)
  const href = submission
    ? `/orgs/${orgId}/forms/${submission.formId}/submissions/${submission.id}`
    : undefined;

  // Separate link for drilling into a manager's team
  const teamHref = member.hasReports
    ? `/orgs/${orgId}/check-ins?view=team&manager=${member.id}`
    : undefined;

  const content = (
    <>
      <div class="size-10 rounded-full flex items-center justify-center bg-surface-muted group-hover:ring-2 group-hover:ring-text-subtle transition-shadow">
        <IcoUser class="size-5 text-text-muted" />
      </div>
      <div class="flex flex-col min-w-0 flex-1">
        <div class="flex items-center gap-2">
          {/* Orange dot for unreviewed */}
          {isUnreviewed && (
            <IcoCircle class="size-2 text-orange-500 fill-current flex-shrink-0" />
          )}
          <span class="font-medium text-text truncate">{displayName}</span>
        </div>
        {member.hasReports && (
          <span class="text-sm text-text-muted">
            <a
              href={teamHref}
              class="inline-flex items-center gap-1 hover:text-[var(--brand-600)] hover:underline transition-colors"
              onClick={(e: Event) => e.stopPropagation()}
            >
              <IcoUsers class="size-3.5" />
              Team ({member.reportCount})
            </a>
          </span>
        )}
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <span
          class={`text-sm ${hasSubmission ? "text-text-muted" : "text-text-subtle"}`}
        >
          {submissionDate}
        </span>
      </div>
    </>
  );

  if (href) {
    return (
      <div
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("a")) return;
          window.location.href = href;
        }}
        class="group flex items-center gap-3 p-3 rounded-xl bg-surface-hover hover:bg-surface-active transition-colors cursor-pointer"
      >
        {content}
      </div>
    );
  }

  // No submission — if manager, make the whole row link to their team
  if (teamHref) {
    return (
      <a
        href={teamHref}
        class="group flex items-center gap-3 p-3 rounded-xl bg-surface-hover hover:bg-surface-active transition-colors"
      >
        {content}
      </a>
    );
  }

  return (
    <div class="flex items-center gap-3 p-3 rounded-xl bg-surface-hover">
      {content}
    </div>
  );
}

// ============================================================================
// My Check-ins View Component
// ============================================================================

function MyCheckInsView({
  orgId,
  pendingForms,
}: {
  orgId: string;
  pendingForms: AssignedForm[];
}) {
  const { history, hasMore, loadingMore, loadMoreError, loadMore } = useHistory(
    {
      orgId,
      initialHistory: [],
      initialHasMore: true,
    },
  );

  // Fetch first page on mount
  useEffect(() => {
    loadMore();
  }, [orgId]);

  function periodEnd(start: Date): Date {
    const d = new Date(start);
    d.setDate(d.getDate() + 6);
    return d;
  }

  // Initial loading state
  if (loadingMore.value && history.value.length === 0) {
    return (
      <div class="bg-surface border border-border rounded-lg p-8 text-center">
        <p class="text-text-muted">Loading check-ins…</p>
      </div>
    );
  }

  if (
    !loadingMore.value &&
    history.value.length === 0 &&
    !hasMore.value &&
    pendingForms.length === 0
  ) {
    return (
      <div class="bg-surface border border-border rounded-lg p-8 text-center">
        <p class="text-text-muted">No check-ins yet.</p>
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-4">
      <PendingCheckInCard forms={pendingForms} />

      {history.value.length > 0 && (
        <>
          <h3 class="text-sm font-medium text-text-muted mt-2">
            Past check-ins
          </h3>
          <table class="w-full text-left text-sm">
            <thead>
              <tr class="border-b border-border text-text-muted">
                <th class="py-2 font-medium">Check-in</th>
                <th class="py-2 font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {history.value.map((item: HistoryItem) => (
                <tr key={item.id} class="border-b border-border">
                  <td class="py-3">
                    <a
                      href={`/orgs/${item.orgId}/forms/${item.formId}?submission=${item.id}`}
                      class="text-text hover:underline"
                    >
                      {formatPeriodRange(
                        item.periodStart,
                        periodEnd(item.periodStart),
                      )}
                    </a>
                  </td>
                  <td class="py-3 text-text-muted">
                    {formatDateShort(item.submittedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {loadMoreError.value && (
        <p class="text-sm text-red-500">{loadMoreError.value}</p>
      )}

      {hasMore.value && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore.value}
          class="self-center px-4 py-2 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
        >
          {loadingMore.value ? "Loading…" : "Load More"}
        </button>
      )}
    </div>
  );
}
